import { ComponentType, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ClockIcon } from "@phosphor-icons/react";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { chapterIcons, defaultChapterIcon, EChapterIcon } from "@libs/zapi-ui/const";
import { DEEP_DIVE_SECTIONS } from "@/zapi-frontend/shared/lib/projectChapters";

export type TQueuedPill = {
  key: string;
  /** Chapter names come from the template; section labels come from i18n via `nameKey`. */
  name?: string;
  nameKey?: (typeof DEEP_DIVE_SECTIONS)[number]["labelKey"];
  icon?: EChapterIcon | null;
  IconComponent?: ComponentType;
};

function PillIcon({ pill }: { pill: TQueuedPill }) {
  if (pill.IconComponent) return <pill.IconComponent />;
  const Icon = pill.icon ? chapterIcons[pill.icon] : chapterIcons[defaultChapterIcon];
  return <Icon className="size-4 shrink-0" color="var(--innovera-zinc900)" weight="regular" />;
}

type TChapterQueuedScreenProps = {
  title: string;
  pills: TQueuedPill[];
  children?: ReactNode;
};

export function ChapterQueuedScreen({ title, pills, children }: TChapterQueuedScreenProps) {
  const { t } = useTranslation("projectHome");

  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-4 px-6">
      <div className="flex items-center justify-center size-11 rounded-full border border-innoveraZinc-300">
        <ClockIcon className="size-6 text-innoveraZinc-400" weight="regular" />
      </div>
      <div className="flex flex-col items-center gap-1.5">
        <FigmaTypography variant="heading-1" className="text-center">
          {t("generation.queuedTitle", { title })}
        </FigmaTypography>
        <FigmaTypography variant="body-2" color="zinc500" className="text-center">
          {pills.length === 0
            ? t("generation.queuedStartsShortly")
            : pills.length === 1
              ? t("generation.queuedStartsAfterOne")
              : t("generation.queuedStartsAfter")}
        </FigmaTypography>
      </div>
      {pills.length > 0 && (
        <div className="flex flex-wrap items-center justify-center gap-2 max-w-[400px]">
          {pills.map((pill) => (
            <div
              key={pill.key}
              className="flex items-center gap-2 h-8 px-3 rounded-lg border border-innoveraZinc-150 bg-white"
            >
              <PillIcon pill={pill} />
              <FigmaTypography variant="label-2">{pill.nameKey ? t(pill.nameKey) : pill.name}</FigmaTypography>
            </div>
          ))}
        </div>
      )}
      {children}
    </div>
  );
}
